import React, { useState, useRef, useEffect, useContext } from "react";
import { useTheme } from "@material-ui/core/styles";

import { useHistory } from "react-router-dom";
import { format } from "date-fns";
// import { SocketContext } from "../../context/Socket/SocketContext";

import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import { Bell as ChatIcon, X as CloseIcon, Trash2 as ClearAllIcon } from "lucide-react";
import { Button, Tooltip, Typography, Box } from "@material-ui/core";

import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import Favicon from "react-favicon";
import { getBackendUrl } from "../../config";
import defaultLogoFavicon from "../../assets/favicon.ico";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import api from "../../services/api";
import usePermissions from "../../hooks/usePermissions";
import ColorModeContext from "../../layout/themeContext";
import NotificationTicketCard from "../NotificationTicketCard";

const useStyles = makeStyles(theme => ({
  tabContainer: {
    overflowY: "auto",
    maxHeight: 380,
    padding: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  popoverPaper: {
    width: "100%",
    maxWidth: 320,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      maxWidth: 280,
    },
  },
  noShadow: {
    boxShadow: "none !important",
  },
  clearAllButton: {
    padding: "8px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
  },
}));

const NotificationsPopOver = ({ volume = 1 }) => {
  const classes = useStyles();
  const theme = useTheme();

  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { colorMode } = useContext(ColorModeContext);
  const { viewMode } = colorMode;
  const { profile, queues } = user;

  const ticketIdUrl = +history.location.pathname.split("/")[2];
  const ticketIdRef = useRef(ticketIdUrl);
  const anchorEl = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [releaseRequests, setReleaseRequests] = useState([]);
  const queueIds = queues.map((q) => q.id);
  const { get: getSetting } = useCompanySettings();
  const { setCurrentTicket, setTabOpen } = useContext(TicketsContext);

  const [showTicketWithoutQueue, setShowTicketWithoutQueue] = useState(false);
  const [showNotificationPending, setShowNotificationPending] = useState(false);
  const [showGroupNotification, setShowGroupNotification] = useState(false);

  const [, setDesktopNotifications] = useState([]);

  const soundTimeoutRef = useRef(null);
  const lastSoundTimeRef = useRef(0);
  const closeTimeoutRef = useRef(null);

  const { hasPermission: checkPerm } = usePermissions();

  const { tickets } = useTickets({
    withUnreadMessages: "true",
    queueIds: JSON.stringify(queueIds),
    showAll: checkPerm("tickets.view-all") ? "true" : "false"
  });

  const [play] = useSound(alertSound, { volume });
  const soundAlertRef = useRef();

  const historyRef = useRef(history);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting(
          {
            "column": "showNotificationPending"
          }
        );

        if (setting && setting.showNotificationPending === true) {
          setShowNotificationPending(true);
        }

        if (checkPerm("tickets.view-all")) {
          setShowTicketWithoutQueue(true);
        }
        if (checkPerm("tickets.view-groups")) {
          setShowGroupNotification(true);
        }
      } catch (err) {
        // 403 = sem permissão settings.view, silencia
        if (err?.response?.status !== 403) {
          toastError(err);
        }
      }
    }

    fetchSettings();
  }, [setShowTicketWithoutQueue, setShowNotificationPending]);

  useEffect(() => {
    soundAlertRef.current = play;

    if (!("Notification" in window)) {
      // Browser não suporta notificações
    } else {
      Notification.requestPermission();
    }
  }, [play]);

  useEffect(() => {
    const processNotifications = () => {
      if (tickets && tickets.length > 0) {
        const groupedNotifications = groupNotificationsByContact(tickets);
        setNotifications(groupedNotifications);
      } else {
        setNotifications([]);
      }
    }

    processNotifications();
  }, [tickets]);

  useEffect(() => {
    ticketIdRef.current = ticketIdUrl;
  }, [ticketIdUrl]);

  useEffect(() => {
    const companyId = user.companyId;
    if (!user.id || !socket) {
      return;
    }

    const onConnectNotificationsPopover = () => {
      socket.emit("joinNotification");
    }

    const onCompanyTicketNotificationsPopover = (data) => {
      if (data.action === "updateUnread" || data.action === "delete") {
        setNotifications(prevState => {
          const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
          if (ticketIndex !== -1) {
            prevState.splice(ticketIndex, 1);
            return [...prevState];
          }
          return prevState;
        });

        setDesktopNotifications(prevState => {
          const notfiticationIndex = prevState.findIndex(
            n => n.tag === String(data.ticketId)
          );
          if (notfiticationIndex !== -1) {
            prevState[notfiticationIndex].close();
            prevState.splice(notfiticationIndex, 1);
            return [...prevState];
          }
          return prevState;
        });
      }
    };

    const onCompanyAppMessageNotificationsPopover = (data) => {
      const message = data?.message;
      const ticket = data?.ticket;
      const hasUnreadSignal = ticket?.unreadMessages > 0 || message?.read === false;
      const isAssignedToCurrentUser = ticket?.userId === user?.id || !ticket?.userId;
      const isQueueAllowed =
        user?.queues?.some(queue => queue.id === ticket?.queueId) ||
        (!ticket?.queueId && showTicketWithoutQueue === true);
      const isStatusAllowed =
        ticket?.status === "open" ||
        ticket?.status === "pending" && showNotificationPending === true ||
        ticket?.status === "group" && ticket?.whatsapp?.groupAsTicket === "enabled" && showGroupNotification === true;

      // FILTRO DE CONEXÕES: Verificar se usuário tem permissão para ver notificações desta conexão
      const hasAllConnectionsPermission = user?.super === true || user?.profile === "admin" ||
        (user?.permissions && user.permissions.includes("all-connections.view"));
      const isConnectionAllowed = hasAllConnectionsPermission ||
        !ticket?.whatsappId || // Se não tem whatsappId, permite (fallback seguro)
        (user?.allowedConnectionIds && user.allowedConnectionIds.includes(ticket.whatsappId));

      if (
        data?.action === "create" &&
        message &&
        ticket &&
        !message.fromMe &&
        hasUnreadSignal &&
        isAssignedToCurrentUser &&
        isQueueAllowed &&
        isStatusAllowed &&
        isConnectionAllowed
      ) {
        setNotifications(prevState => {
          const ticketIndex = prevState.findIndex(t => t.id === ticket.id);
          if (ticketIndex !== -1) {
            const updatedTicket = { ...prevState[ticketIndex] };
            // Soma mensagens não lidas
            updatedTicket.unreadMessages = (updatedTicket.unreadMessages || 0) + (ticket.unreadMessages || 1);
            // Atualiza para mensagem mais recente
            updatedTicket.lastMessage = ticket.lastMessage || updatedTicket.lastMessage;
            updatedTicket.updatedAt = ticket.updatedAt;
            
            const newState = [...prevState];
            newState[ticketIndex] = updatedTicket;
            // Reordena por data
            return newState.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
          }
          return [ticket, ...prevState];
        });

        const shouldNotNotificate =
          (message.ticketId === ticketIdRef.current &&
            document.visibilityState === "visible") ||
          (ticket.userId && ticket.userId !== user?.id) ||
          (ticket.isGroup && ticket?.whatsapp?.groupAsTicket === "disabled" && showGroupNotification === false);


        if (shouldNotNotificate === true) return;

        handleNotifications(data);
      }
    }

    const onCompanyContactReleaseRequest = (data) => {
      if (profile !== "admin") return;
      if (!data || !data.action) return;
      if (data.action === "create" && data.record) {
        setReleaseRequests(prev => {
          const exists = prev.some(r => r.id === data.record.id);
          if (exists) {
            return prev.map(r => r.id === data.record.id ? data.record : r);
          }
          return [data.record, ...prev];
        });
      }
      if (data.action === "resolve" && data.record?.id) {
        setReleaseRequests(prev => prev.filter(r => r.id !== data.record.id));
      }
    };

    // Registrar listeners
    socket.on("connect", onConnectNotificationsPopover);
    socket.on(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
    socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);
    socket.on(`company-${companyId}-contactReleaseRequest`, onCompanyContactReleaseRequest);

    // Emitir join ao montar (caso já esteja conectado)
    if (socket.connected) {
      socket.emit("joinNotification");
    }

    return () => {
      socket.off("connect", onConnectNotificationsPopover);
      socket.off(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
      socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);
      socket.off(`company-${companyId}-contactReleaseRequest`, onCompanyContactReleaseRequest);
      if (soundTimeoutRef.current) {
        clearTimeout(soundTimeoutRef.current);
      }
    };
  }, [user?.id, user?.companyId, profile, queues, showTicketWithoutQueue, socket, showNotificationPending, showGroupNotification]);

  useEffect(() => {
    const fetchReleaseRequests = async () => {
      try {
        if (profile !== "admin") return;
        const { data } = await api.get("/contact-release-requests");
        const records = Array.isArray(data?.records) ? data.records : [];
        setReleaseRequests(records);
      } catch (err) {
        // Não quebrar o popover por falha de permissão/configuração
      }
    };
    if (user?.id) {
      fetchReleaseRequests();
    }
  }, [user?.id, profile]);

  const handleResolveReleaseRequest = async (id) => {
    try {
      await api.post(`/contact-release-requests/${id}/resolve`);
      setReleaseRequests(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenContactFromRelease = (contactId) => {
    setIsOpen(false);
    history.push(`/contacts?openContactId=${contactId}`);
  };

  const handleNotifications = data => {
    const { message, ticket } = data;
    const contact = data?.contact || data?.ticket?.contact || {};

    try {
      if ("Notification" in window && Notification.permission === "granted") {
        const options = {
          body: `${message.body} - ${format(new Date(), "HH:mm")}`,
          icon: contact.urlPicture 
            ? (contact.urlPicture.startsWith('http') ? contact.urlPicture : `${getBackendUrl()}${contact.urlPicture}`)
            : null,
          tag: ticket.id,
          renotify: true,
        };
        const notification = new Notification(
          `${i18n.t("tickets.notification.message")} ${contact.name || "Contato"}`,
          options
        );

        notification.onclick = e => {
          e.preventDefault();
          window.focus();
          setTabOpen(ticket.status)
          historyRef.current.push(`/tickets/${ticket.uuid}`);
        };

        setDesktopNotifications(prevState => {
          const notfiticationIndex = prevState.findIndex(
            n => n.tag === notification.tag
          );
          if (notfiticationIndex !== -1) {
            prevState[notfiticationIndex] = notification;
            return [...prevState];
          }
          return [notification, ...prevState];
        });
      }
    } catch (error) {
      console.warn("[Notifications] Falha ao criar notificação desktop", error);
    }

    const now = Date.now();
    const timeSinceLastSound = now - lastSoundTimeRef.current;
    const MIN_SOUND_INTERVAL = 1000; // Mínimo 1 segundo entre sons

    if (timeSinceLastSound < MIN_SOUND_INTERVAL) {
      if (soundTimeoutRef.current) {
        clearTimeout(soundTimeoutRef.current);
      }
      soundTimeoutRef.current = setTimeout(() => {
        soundAlertRef.current?.();
        lastSoundTimeRef.current = Date.now();
      }, MIN_SOUND_INTERVAL - timeSinceLastSound);
    } else {
      soundAlertRef.current?.();
      lastSoundTimeRef.current = now;
    }
  };

  // Função para agrupar notificações por contato/grupo
  const groupNotificationsByContact = (ticketsList) => {
    if (!Array.isArray(ticketsList)) return [];
    
    const grouped = {};
    
    ticketsList.forEach(ticket => {
      // Usa contactId ou ticket.contact.id como chave
      const contactId = ticket.contact?.id || ticket.contactId || ticket.id;
      const isGroup = ticket.isGroup === true;
      const key = `${contactId}-${isGroup ? 'group' : 'private'}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          ...ticket,
          unreadMessages: Number(ticket.unreadMessages) || 0,
          _originalTickets: [ticket]
        };
      } else {
        // Soma mensagens não lidas
        grouped[key].unreadMessages += Number(ticket.unreadMessages) || 0;
        grouped[key]._originalTickets.push(ticket);
        
        // Mantém a mensagem mais recente (última atualização)
        const currentDate = new Date(ticket.updatedAt || Date.now());
        const existingDate = new Date(grouped[key].updatedAt || 0);
        
        if (currentDate > existingDate) {
          grouped[key].lastMessage = ticket.lastMessage;
          grouped[key].updatedAt = ticket.updatedAt;
          grouped[key].id = ticket.id;
          grouped[key].uuid = ticket.uuid;
          grouped[key].status = ticket.status;
        }
      }
    });
    
    // Converte de volta para array e ordena por data (mais recente primeiro)
    return Object.values(grouped).sort((a, b) => {
      return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
    });
  };

  const handleClick = () => {
    setIsOpen(prevState => !prevState);
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 800);
  };

  const browserNotification = () => {
    const numbers = "⓿➊➋➌➍➎➏➐➑➒➓⓫⓬⓭⓮⓯⓰⓱⓲⓳⓴";
    if (notifications.length > 0) {
      if (notifications.length < 21) {
        document.title = numbers.substring(notifications.length, notifications.length + 1) + " - " + (theme.appName || "...");
      } else {
        document.title = "(" + notifications.length + ")" + (theme.appName || "...");
      }
    } else {
      document.title = theme.appName || "...";
    }
    return (
      <>
        <Favicon
          animated={true}
          url={(theme?.appLogoFavicon) ? theme.appLogoFavicon : defaultLogoFavicon}
          alertCount={notifications.length}
          iconSize={195}
        />
      </>
    );
  };

  return (
    <>
      {browserNotification()}

      <Tooltip title={i18n.t("dashboard.buttons.notifications")} arrow>
        <IconButton
          onClick={handleClick}
          ref={anchorEl}
          aria-label="Open Notifications"
          color="inherit"
          style={{ padding: 8, color: viewMode === "modern" ? "var(--text)" : "white" }}
        >
          <Badge
            overlap="rectangular"
            badgeContent={notifications.length + (profile === "admin" ? releaseRequests.length : 0)}
            color="secondary"
          >
            <ChatIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
        }}
      >
        <Box className={classes.clearAllButton}>
          <Typography variant="body2" color="textSecondary">
            {notifications.length} notificação(ões)
          </Typography>
          {notifications.length > 0 && (
            <Button
              size="small"
              color="secondary"
              startIcon={<ClearAllIcon size={16} />}
              onClick={() => {
                setNotifications([]);
                api.post("/tickets/mark-all-as-read");
              }}
            >
              Limpar todas
            </Button>
          )}
        </Box>
        <List dense className={classes.tabContainer}>
          {profile === "admin" && releaseRequests.length > 0 && (
            <>
              <ListItem>
                <ListItemText primary="Solicitações de liberação" />
              </ListItem>
              {releaseRequests.map(req => (
                <ListItem key={`release-${req.id}`} divider>
                  <ListItemText
                    primary={`Contato bloqueado: ${req?.contact?.name || req?.contactId}`}
                    secondary={`Solicitado por: ${req?.requester?.name || req?.requesterId}`}
                    onClick={() => handleOpenContactFromRelease(req.contactId)}
                    style={{ cursor: "pointer" }}
                  />
                  <ListItemSecondaryAction>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => handleResolveReleaseRequest(req.id)}
                    >
                      Resolver
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              <ListItem>
                <ListItemText primary="Notificações de tickets" />
              </ListItem>
            </>
          )}
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
            </ListItem>
          ) : (
            notifications.map(ticket => (
              <NotificationTicketCard
                key={ticket.id}
                ticket={ticket}
                onClick={(t) => {
                  setTabOpen(t.status);
                  setCurrentTicket({ id: t.id, uuid: t.uuid, code: Date.now() });
                  history.push(`/tickets/${t.uuid}`);
                  setIsOpen(false);
                }}
                onClose={async (t) => {
                  try {
                    await api.post(`/tickets/${t.id}/mark-as-read`);
                    setNotifications(prev => prev.filter(nt => nt.id !== t.id));
                  } catch (err) {
                    if (err?.response?.status !== 403) {
                      toastError(err);
                    }
                  }
                }}
              />
            ))
          )}
        </List>
      </Popover>
    </>
  );
};

export default NotificationsPopOver;